<script setup lang="ts">
import type { Swiper } from '#components'
import { API_GET_TAG_GAMES } from '@/api'
import type * as API_TYPE from '@/types/api'
import SVG_TAG_MORE_GAME from '~/assets/svg/tag-more-game.svg'

interface Props {
  tags: API_TYPE.IGameData['gameCategory']
}

const props = defineProps<Props>()

const swiperInstance = ref()
const swiperAttrs = reactive<InstanceType<typeof Swiper>['$attrs']>({
  modules: [SwiperNavigation],
  navigation: {
    disabledClass: 'op-0 pointer-events-none',
    prevEl: '.prev',
    nextEl: '.next',
  },
  slidesPerView: 'auto',
  slidesPerGroup: 3,
  // render渲染时候有问题，直接在css上加
  spaceBetween: 20,
})

const route = useRoute()
const tailRef = ref()
const { height } = useElementSize(tailRef)
// 根据是否高度为0来判断是否隐藏
const tailVisible = computed(() => !!height.value)

const currentTag = ref(props.tags[0])
async function switchTag(tag: API_TYPE.IGameData['gameCategory'][number]) {
  // 如果没查过的标签需要先查询
  if (tag.games === undefined) {
    const tagGames = await API_GET_TAG_GAMES(tag.categoryName)
    tag.games = tagGames.games.filter(i => i.seo !== route.params.name).slice(0, 13)
  }
  currentTag.value = tag
}
</script>

<template>
  <div class="relative mt-10 w-full" v-bind="$attrs">
    <Swiper v-bind="swiperAttrs" :class="!swiperInstance && '[&>.swiper-wrapper]:flex [&>.swiper-wrapper]:gap-5'" @swiper="swiperInstance = $event">
      <SwiperSlide v-for="v in tags" :key="v.id" class="w-fit!">
        <CommonTagBlock v-bind="v" to="" :active="currentTag === v" @click="switchTag(v)" />
      </SwiperSlide>
    </Swiper>

    <div class="prev absolute z-1 flex transition-all-300" c="#fff op-90" inset="is-0 bs-0">
      <div class="bg-gray-1 pie-4">
        <button class="ld-button rd-full p-3.25 c-current lt-md:hidden ld-button-black" @click="swiperInstance?.slidePrev()">
          <svg class="size-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.92375 8.76822L10.3014 15.7496C11.075 16.3942 12.2087 15.6204 11.8903 14.6651L10.16 9.47434C10.0605 9.17573 9.82615 8.94142 9.52755 8.84188L7.00189 8L9.52755 7.15811C9.82615 7.05858 10.0605 6.82426 10.16 6.52566L11.8903 1.33485C12.2087 0.37959 11.075 -0.394222 10.3014 0.2504L1.92376 7.23178C1.444 7.63157 1.444 8.36842 1.92375 8.76822Z" fill="currentColor" />
          </svg>
        </button>
      </div>
      <div class="size-11 from-gray-1 to-transparent" bg="gradient-to-r" />
    </div>

    <div class="next absolute z-1 flex transition-all-100" c="#fff op-90" inset="ie-0 bs-0">
      <div class="size-11 from-gray-1 to-transparent" bg="gradient-to-l" />
      <div class="bg-gray-1 pis-4">
        <button class="ld-button rd-full p-3.25 c-current lt-md:hidden ld-button-black" @click="swiperInstance?.slideNext()">
          <svg class="size-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.0762 8.76822L5.6986 15.7496C4.92505 16.3942 3.79131 15.6204 4.10973 14.6651L5.84 9.47434C5.93953 9.17573 6.17385 8.94142 6.47245 8.84188L8.99811 8L6.47245 7.15811C6.17385 7.05858 5.93953 6.82426 5.84 6.52566L4.10973 1.33485C3.79131 0.37959 4.92504 -0.394222 5.69859 0.2504L14.0762 7.23178C14.556 7.63157 14.556 8.36842 14.0762 8.76822Z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
  </div>

  <div class="mt-10" v-bind="$attrs">
    <section class="grid grid-auto-rows-0 mt--2 of-hidden pt-2" gap="4 md:x-6 md:y-0" grid-rows="4 md:1" grid-cols="3 md:5 lg:6 xl:8 2xl:10 4k:14">
      <CommonGameLite v-for="v in currentTag?.games || []" :key="v.gameId" :game="v" />

      <NuxtLink ref="tailRef" class="bg-gray-1 transition-all-300 hover:-translate-y-2" :to="`/tags/${currentTag.categoryName}.html`" target="_blank">
        <div class="flex-shrink-0" lt-md="rd-3" md="rd-4">
          <SVG_TAG_MORE_GAME class="aspect-ratio-1 h-full" />
        </div>
        <span class="line-clamp-2 mbs-2 break-words text-3.5 c-#fff font-600 lh-4 md:mbs-3">
          {{ $t('gameDetail.moreGames') }}
        </span>
      </NuxtLink>

      <ClientOnly>
        <NuxtLink v-if="!tailVisible" class="grid-area-[span_1/span_1/-1/-1] bg-gray-1 transition-all-300 hover:-translate-y-2" :to="`/tags/${currentTag.categoryName}.html`" target="_blank">
          <div class="flex-shrink-0" lt-md="rd-3" md="rd-4">
            <SVG_TAG_MORE_GAME class="aspect-ratio-1 h-full" />
          </div>
          <span class="line-clamp-2 mbs-2 break-words text-3.5 c-#fff font-600 lh-4 md:mbs-3">
            {{ $t('gameDetail.moreGames') }}
          </span>
        </NuxtLink>
      </ClientOnly>
    </section>
  </div>
</template>

<style lang="scss" scoped>
</style>
